import React, { useEffect, useState } from 'react';
import { useCart } from '../CartContext';
import { useNavigate } from 'react-router-dom';
import { placeOrder, getToken, createPayment } from '../api';
import { showError, showWarning } from '../components/AlertModal';

const steps = ['Cart Review', 'Delivery Info', 'Payment', 'Confirmation'];

function StepIndicator({ current }) {
  const [stepMobile, setStepMobile] = useState(window.innerWidth <= 480);
  useEffect(() => {
    const h = () => setStepMobile(window.innerWidth <= 480);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: stepMobile ? '1.5rem' : '2.5rem', flexWrap: 'wrap', rowGap: '12px' }}>
      {steps.map((step, i) => (
        <React.Fragment key={step}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: stepMobile ? '4px' : '6px' }}>
            <div style={{
              width: stepMobile ? 30 : 36, height: stepMobile ? 30 : 36, borderRadius: '50%',
              background: i < current ? '#16a34a' : i === current ? '#16a34a' : '#e5e7eb',
              color: i <= current ? 'white' : '#9ca3af',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: stepMobile ? '0.75rem' : '0.875rem',
              transition: 'all 0.3s',
            }}>
              {i < current
                ? <svg xmlns="http://www.w3.org/2000/svg" width={stepMobile ? 12 : 16} height={stepMobile ? 12 : 16} viewBox="0 0 24 24" strokeWidth="3" stroke="white" fill="none"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 12l5 5l10 -10"/></svg>
                : i + 1}
            </div>
            <span style={{ fontSize: stepMobile ? '0.625rem' : '0.75rem', fontWeight: i === current ? 700 : 400, color: i === current ? '#16a34a' : i < current ? '#374151' : '#9ca3af', whiteSpace: 'nowrap' }}>
              {step}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div style={{ height: 2, width: stepMobile ? 28 : 60, background: i < current ? '#16a34a' : '#e5e7eb', margin: '0 2px', marginBottom: stepMobile ? '18px' : '22px', transition: 'background 0.3s', flexShrink: 0 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Step 0: Cart Review ───────────────────────────────────────────────────────
function CartReviewStep({ onNext, onBack }) {
  const { cartItems, removeFromCart, updateQty, cartTotal, getDeliveryCharge, getFinalTotal } = useCart();
  const deliveryCharge = getDeliveryCharge();
  const finalTotal = getFinalTotal();

  // Track mobile viewport
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (cartItems.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🛒</div>
        <h3 style={{ fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Your cart is empty</h3>
        <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>Add some products before checking out.</p>
        <button onClick={onBack} style={{ padding: '11px 24px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>Continue Shopping</button>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontWeight: 700, fontSize: isMobile ? '1rem' : '1.125rem', color: '#111827', marginBottom: '1.25rem' }}>Review Your Cart</h2>

      {/* ── Desktop: Table Grid ── */}
      {!isMobile && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', marginBottom: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '10px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', gap: '1rem' }}>
            {['Product', 'Price', 'Quantity', 'Subtotal'].map(h => (
              <span key={h} style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{h}</span>
            ))}
          </div>
          {cartItems.map(item => (
            <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '16px', borderBottom: '1px solid #f3f4f6', gap: '1rem', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img src={item.image} alt={item.name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827', marginBottom: '2px' }}>{item.name}{item.unit ? ` (${item.unit})` : ''}</div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '4px' }}>{item.category}</div>
                  <button onClick={() => removeFromCart(item.id, item.isB2B)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.75rem', padding: 0, fontWeight: 500, minHeight: 'auto', minWidth: 'auto' }}>✕ Remove</button>
                </div>
              </div>
              <span style={{ fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>₹{item.price}{item.unit ? `/${item.unit}` : ''}</span>
              <div className="qty-control" style={{ width: 'fit-content' }}>
                <button className="qty-btn" onClick={() => updateQty(item.id, -1, item.isB2B, item.minOrder || 1)} disabled={item.isB2B && item.qty <= (item.minOrder || 1)} style={item.isB2B && item.qty <= (item.minOrder || 1) ? { opacity: 0.35, cursor: 'not-allowed' } : {}}>−</button>
                <span className="qty-value">{item.qty}</span>
                <button className="qty-btn" onClick={() => updateQty(item.id, 1, item.isB2B, item.minOrder || 1)}>+</button>
              </div>
              <span style={{ fontWeight: 700, color: '#111827', fontSize: '0.9rem' }}>₹{(item.price * item.qty).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Mobile: Card Layout ── */}
      {isMobile && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '1.5rem' }}>
          {cartItems.map(item => (
            <div key={item.id} style={{
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '14px',
              background: '#fff',
            }}>
              {/* Top row: Image + Info */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <img
                  src={item.image} alt={item.name}
                  style={{
                    width: 64, height: 64, objectFit: 'cover',
                    borderRadius: '10px', border: '1px solid #e5e7eb', flexShrink: 0
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 600, fontSize: '0.875rem', color: '#111827',
                    marginBottom: '3px', lineHeight: 1.3,
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                  }}>
                    {item.name}{item.unit ? ` (${item.unit})` : ''}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginBottom: '4px' }}>{item.category}</div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#16a34a' }}>
                    ₹{item.price}{item.unit ? `/${item.unit}` : ''}
                  </div>
                </div>
              </div>

              {/* Bottom row: Qty + Subtotal + Remove */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderTop: '1px solid #f3f4f6', paddingTop: '10px'
              }}>
                <div className="qty-control" style={{ width: 'fit-content' }}>
                  <button className="qty-btn" onClick={() => updateQty(item.id, -1, item.isB2B, item.minOrder || 1)} disabled={item.isB2B && item.qty <= (item.minOrder || 1)} style={item.isB2B && item.qty <= (item.minOrder || 1) ? { opacity: 0.35, cursor: 'not-allowed' } : {}}>−</button>
                  <span className="qty-value">{item.qty}</span>
                  <button className="qty-btn" onClick={() => updateQty(item.id, 1, item.isB2B, item.minOrder || 1)}>+</button>
                </div>
                <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#111827' }}>
                  ₹{(item.price * item.qty).toFixed(2)}
                </span>
                <button
                  onClick={() => removeFromCart(item.id, item.isB2B)}
                  style={{
                    background: '#fef2f2', border: '1px solid #fecaca',
                    borderRadius: '6px', cursor: 'pointer', color: '#ef4444',
                    fontSize: '0.72rem', padding: '5px 10px', fontWeight: 600,
                    minHeight: 'auto', minWidth: 'auto',
                    display: 'flex', alignItems: 'center', gap: '3px'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Order Summary ── */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{
          border: '1px solid #e5e7eb', borderRadius: '10px',
          padding: isMobile ? '1rem' : '1.25rem 1.5rem',
          width: isMobile ? '100%' : 'auto',
          minWidth: isMobile ? 'auto' : 280,
          maxWidth: isMobile ? '100%' : 400
        }}>
          <h3 style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#111827', marginBottom: '1rem' }}>Order Summary</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '1rem' }}>
            {[['Item Total (' + cartItems.length + ' item' + (cartItems.length !== 1 ? 's' : '') + ')', `₹${cartTotal.toFixed(2)}`], ['Delivery Charges', deliveryCharge > 0 ? `₹${deliveryCharge.toFixed(2)}` : 'FREE'], ['GST', '₹0.00']].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', color: '#6b7280' }}>
                <span>{l}</span><span style={{ color: v === 'FREE' ? '#16a34a' : '#374151', fontWeight: v === 'FREE' ? 600 : 400 }}>{v}</span>
              </div>
            ))}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem', color: '#111827' }}>
              <span>Grand Total</span><span>₹{finalTotal.toFixed(2)}</span>
            </div>
          </div>
          <button onClick={onNext} style={{ width: '100%', padding: '12px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '0.9375rem', transition: 'background 0.2s' }}>
            Proceed to Delivery →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Step 1: Delivery Info ─────────────────────────────────────────────────────
function DeliveryStep({ form, setForm, onNext, onBack }) {
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [locationDetails, setLocationDetails] = useState(null);
  const [detectedAddressDraft, setDetectedAddressDraft] = useState(null);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  // Hyderabad coordinates (approximate center)
  const HYDERABAD_BOUNDS = {
    minLat: 17.3000,
    maxLat: 17.5500,
    minLng: 78.3500,
    maxLng: 78.5500
  };

  // Hyderabad valid PIN codes (starting with 500)
  const HYDERABAD_PIN_CODES = [
    '500001', '500002', '500003', '500004', '500005', '500006', '500007', '500008', '500009', '500010',
    '500011', '500012', '500013', '500014', '500015', '500016', '500017', '500018', '500019', '500020',
    '500021', '500022', '500023', '500024', '500025', '500026', '500027', '500028', '500029', '500030',
    '500031', '500032', '500033', '500034', '500035', '500036', '500037', '500038', '500039', '500040',
    '500041', '500042', '500043', '500044', '500045', '500046', '500047', '500048', '500049', '500050',
    '500051', '500052', '500053', '500054', '500055', '500056', '500057', '500058', '500059', '500060',
    '500061', '500062', '500063', '500064', '500065', '500066', '500067', '500068', '500069', '500070',
    '500071', '500072', '500073', '500074', '500075', '500076', '500077', '500078', '500079', '500080',
    '500081', '500082', '500083', '500084', '500085', '500086', '500087', '500088', '500089', '500090',
    '500091', '500092', '500093', '500094', '500095', '500096', '500097', '500098', '500099', '500100'
  ];

  // List of Hyderabad area keywords
  const HYDERABAD_KEYWORDS = [
    'hyderabad', 'secunderabad', 'hitech city', 'gachibowli', 'madhapur',
    'kondapur', 'jubilee hills', 'banjara hills', 'ameerpet', 'begumpet',
    'sainikpuri', 'malkajgiri', 'uppal', 'dilsukhnagar', 'kukatpally',
    'miyapur', 'chandanagar', 'lingampally', 'tellapur', 'nallagandla',
    'manikonda', 'kothaguda', 'raidurg', 'hafeezpet', 'film nagar', 
    'panjagutta', 'somajiguda', 'lakdi ka pul', 'khairatabad', 'toli chowki',
    'mehdipatnam', 'attapur', 'rajendra nagar', 'vanasthalipuram', 'lb nagar'
  ];

  // Validate PIN code for Hyderabad
  const isValidHyderabadPin = (pin) => {
    return HYDERABAD_PIN_CODES.includes(pin);
  };

  const searchHyderabadLocations = async (query) => {
    if (!query || query.trim().length < 3) {
      setLocationSuggestions([]);
      return;
    }
    setLocationSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query + ', Hyderabad, Telangana, India')}&limit=6&addressdetails=1&countrycodes=in`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await response.json();
      const hydResults = (Array.isArray(data) ? data : []).filter((p) => {
        const addr = p.address || {};
        const city = (addr.city || addr.town || addr.village || '').toLowerCase();
        const county = (addr.county || '').toLowerCase();
        const state = (addr.state || '').toLowerCase();
        return (city.includes('hyderabad') || county.includes('hyderabad') || state.includes('telangana')) && addr.country_code === 'in';
      });
      setLocationSuggestions(hydResults);
      setShowLocationSuggestions(true);
    } catch (e) {
      setLocationSuggestions([]);
    } finally {
      setLocationSearching(false);
    }
  };

  const handleLocationSearchChange = (e) => {
    const value = e.target.value;
    setLocationSearch(value);
    if (value.trim().length < 3) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }
    searchHyderabadLocations(value);
  };

  const handleSelectLocationSuggestion = (place) => {
    const addr = place.address || {};
    const pincode = (addr.postcode || '').replace(/\D/g, '').slice(0, 6);
    const area = addr.suburb || addr.neighbourhood || addr.quarter || '';
    const road = addr.road || addr.footway || addr.pedestrian || '';
    const city = addr.city || addr.town || addr.village || 'Hyderabad';
    const state = addr.state || 'Telangana';

    setForm((prev) => ({
      ...prev,
      address1: road || area || prev.address1 || '',
      address2: area && road ? area : (prev.address2 || ''),
      city: city || 'Hyderabad',
      state: state || 'Telangana',
      pin: pincode || prev.pin || '',
      landmark: prev.landmark || area || '',
    }));

    setLocationSearch(place.display_name || '');
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);

    if (pincode && !isValidHyderabadPin(pincode)) {
      setLocationError(`⚠️ Found address, but PIN ${pincode} is outside allowed Hyderabad range. Please verify.`);
    } else {
      setLocationError('✅ Address selected from Hyderabad search.');
      setTimeout(() => setLocationError(''), 2500);
    }
  };

  // Check if location is within Hyderabad
  const isWithinHyderabad = (city, state, pin, lat, lng) => {
    // Check by PIN code first (most reliable)
    if (pin && isValidHyderabadPin(pin)) {
      return true;
    }
    
    // Check by coordinates
    if (lat && lng) {
      const isWithinBounds = lat >= HYDERABAD_BOUNDS.minLat && lat <= HYDERABAD_BOUNDS.maxLat &&
                            lng >= HYDERABAD_BOUNDS.minLng && lng <= HYDERABAD_BOUNDS.maxLng;
      if (isWithinBounds) return true;
    }

    // Check by city/state name
    const locationStr = `${city} ${state}`.toLowerCase();
    for (const keyword of HYDERABAD_KEYWORDS) {
      if (locationStr.includes(keyword.toLowerCase())) {
        return true;
      }
    }
    return false;
  };

  // Get detailed address using multiple APIs
  const getDetailedAddress = async (latitude, longitude) => {
    // Try OpenStreetMap Nominatim first (best for detailed street addresses)
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1&zoom=18&accept-language=en`,
        {
          headers: {
            'User-Agent': 'ePayhub Store-App/1.0'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        const address = data.address;
        
        // Build complete street address
        const houseNumber = address.house_number || '';
        const road = address.road || address.street || '';
        const suburb = address.suburb || address.neighbourhood || '';
        const city = address.city || address.town || address.village || '';
        const state = address.state || '';
        const pin = address.postcode || '';
        const country = address.country || '';
        
        // Format address line 1 (house number + road)
        let addressLine1 = '';
        if (houseNumber && road) {
          addressLine1 = `${houseNumber}, ${road}`;
        } else if (road) {
          addressLine1 = road;
        } else if (suburb) {
          addressLine1 = suburb;
        }
        
        // Address line 2 (suburb/area)
        const addressLine2 = suburb && !addressLine1.includes(suburb) ? suburb : '';
        
        return {
          address1: addressLine1,
          address2: addressLine2,
          city: city,
          state: state,
          pin: pin,
          country: country,
          fullAddress: data.display_name,
          success: true
        };
      }
    } catch (error) {
      console.error('Nominatim API failed:', error);
    }

    // Fallback to BigDataCloud
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        const city = data.city || data.locality || '';
        const state = data.principalSubdivision || '';
        const pin = data.postcode || '';
        const locality = data.locality || data.city || '';
        
        return {
          address1: locality,
          address2: '',
          city: city,
          state: state,
          pin: pin,
          country: data.countryName || '',
          fullAddress: `${locality}, ${city}, ${state} - ${pin}`,
          success: true
        };
      }
    } catch (error) {
      console.error('BigDataCloud API failed:', error);
    }

    // Third fallback - PostPIN API for PIN code lookup
    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${Math.floor(latitude * 100)}${Math.floor(longitude * 100)}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data[0]?.Status === 'Success' && data[0]?.PostOffice?.length > 0) {
          const postOffice = data[0].PostOffice[0];
          return {
            address1: '',
            address2: '',
            city: postOffice.District || '',
            state: postOffice.State || '',
            pin: postOffice.Pincode || '',
            country: 'India',
            fullAddress: `${postOffice.Name}, ${postOffice.District}, ${postOffice.State} - ${postOffice.Pincode}`,
            success: true
          };
        }
      }
    } catch (error) {
      console.error('PostPIN API failed:', error);
    }

    return { success: false };
  };

  const fetchCurrentLocation = () => {
    setLocationLoading(true);
    setLocationError('');
    setLocationDetails(null);
    
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser. Please enter your address manually.');
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const addressData = await getDetailedAddress(latitude, longitude);
        
        if (addressData.success) {
          const { address1, address2, city, state, pin } = addressData;
          
          if (isWithinHyderabad(city, state, pin, latitude, longitude)) {
            // Auto-apply directly to form fields
            setForm(prev => ({
              ...prev,
              address1: address1 || prev.address1 || '',
              city:     city    || 'Hyderabad',
              state:    state   || 'Telangana',
              pin:      pin     || prev.pin || '',
            }));
            setLocationDetails(addressData);
            setLocationError('✅ Location detected and applied.');
            setTimeout(() => setLocationError(''), 3000);
          } else {
            setLocationError(`❌ We deliver only within Hyderabad. Detected: ${city || 'Unknown'}, ${state || ''}. Please enter manually.`);
          }
        } else {
          setLocationError('Unable to detect location. Please enter manually.');
        }
        setLocationLoading(false);
      },
      (error) => {
        const msgs = { 1: 'Location access denied.', 2: 'Location unavailable.', 3: 'Request timed out.' };
        setLocationError((msgs[error.code] || 'Unable to get location.') + ' Please enter manually.');
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  
  const handlePinChange = (e) => {
    const pin = e.target.value;
    setForm(prev => ({ ...prev, pin: pin }));
    
    // Validate PIN code on the fly
    if (pin.length === 6) {
      if (isValidHyderabadPin(pin)) {
        setLocationError('✅ Valid Hyderabad PIN code');
        setTimeout(() => setLocationError(''), 2000);
      } else if (pin.match(/^\d{6}$/)) {
        setLocationError('⚠️ This PIN code may not be in Hyderabad. We only deliver within Hyderabad.');
      }
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate PIN code
    const pin = form.pin || '';
    if (pin && !isValidHyderabadPin(pin)) {
      setLocationError('❌ Invalid PIN code for Hyderabad delivery. Please enter a valid Hyderabad PIN code (starts with 500).');
      return;
    }
    
    // Validate if address is in Hyderabad before proceeding
    const city = form.city?.toLowerCase() || '';
    const state = form.state?.toLowerCase() || '';
    const address = `${form.address1 || ''} ${form.address2 || ''} ${city} ${state}`.toLowerCase();
    
    let isHyd = false;
    for (const keyword of HYDERABAD_KEYWORDS) {
      if (address.includes(keyword.toLowerCase())) {
        isHyd = true;
        break;
      }
    }
    
    if (!isHyd && !isValidHyderabadPin(pin)) {
      setLocationError('❌ We currently deliver only within Hyderabad city limits. Please enter a valid Hyderabad address or PIN code (starts with 500).');
      return;
    }
    
    onNext();
  };

  const applyDetectedAddress = () => {
    if (!detectedAddressDraft) return;
    setForm(prev => ({
      ...prev,
      ...detectedAddressDraft,
      locationFetched: true
    }));
    setLocationError('✅ Detected address applied. You can still edit any field below.');
    setTimeout(() => setLocationError(''), 3000);
  };
  
  const inputStyle = { 
    width: '100%', 
    padding: '12px 14px', 
    border: '1px solid #d1d5db', 
    borderRadius: '8px', 
    fontSize: '0.875rem', 
    outline: 'none', 
    boxSizing: 'border-box', 
    transition: 'border-color 0.15s',
    background: '#fff'
  };

  const fields = [
    [
      { label: 'First Name', name: 'firstName', type: 'text', placeholder: 'Mounika', required: true },
      { label: 'Last Name',  name: 'lastName',  type: 'text', placeholder: 'V',       required: false },
    ],
    [
      { label: 'Phone Number',   name: 'phone',    type: 'tel',  placeholder: '+91 98765 43210',       required: true },
      { label: 'Area/LandMark', name: 'address1', type: 'text', placeholder: 'House No., Street Name', required: true },
    ],
    [
      { label: 'City',  name: 'city',  type: 'text', placeholder: 'Hyderabad', required: true },
      { label: 'State', name: 'state', type: 'text', placeholder: 'Telangana', required: true },
    ],
  ];
  
  return (
    <form onSubmit={handleSubmit}>
      <h2 style={{ fontWeight: 700, fontSize: '1.125rem', color: '#111827', marginBottom: '1.25rem' }}>Delivery Information</h2>

     

      {/* Location search + detect — compact card */}
      <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '1rem', marginBottom: '1.25rem', background: '#fafafa' }}>
      

        {/* Search input */}
        <div style={{ position: 'relative', marginBottom: '10px' }}>
          <input
            type="text"
            value={locationSearch}
            onChange={handleLocationSearchChange}
            onFocus={() => locationSuggestions.length > 0 && setShowLocationSuggestions(true)}
            placeholder="Search area or street in Hyderabad..."
            style={{ ...inputStyle, paddingLeft: '36px', fontSize: '0.8125rem' }}
          />
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.9rem', pointerEvents: 'none' }}>🔍</span>
          {locationSearching && (
            <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.72rem', color: '#9ca3af' }}>Searching...</span>
          )}
          {showLocationSuggestions && locationSuggestions.length > 0 && (
            <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', marginTop: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              {locationSuggestions.map((place, idx) => (
                <button
                  key={`${place.place_id}-${idx}`}
                  type="button"
                  onClick={() => handleSelectLocationSuggestion(place)}
                  style={{ width: '100%', textAlign: 'left', border: 'none', background: '#fff', padding: '9px 12px', borderBottom: idx < locationSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#374151' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  📍 {place.display_name.split(',').slice(0, 4).join(',')}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detect location button */}
        <button
          type="button"
          onClick={fetchCurrentLocation}
          disabled={locationLoading}
          style={{
            width: '100%', padding: '9px 16px',
            background: locationLoading ? '#e5e7eb' : '#fff',
            color: locationLoading ? '#9ca3af' : '#16a34a',
            border: '1px solid #d1d5db', borderRadius: '8px',
            fontWeight: 600, fontSize: '0.8125rem', cursor: locationLoading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          {locationLoading ? (
            <>
              <div style={{ width: '14px', height: '14px', border: '2px solid #9ca3af', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              Detecting location...
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" fill="none">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/>
                <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z"/>
              </svg>
              Use My Current Location
            </>
          )}
        </button>

        {/* Status message */}
        {locationError && (
          <div style={{
            marginTop: '8px', padding: '8px 12px', borderRadius: '6px', fontSize: '0.75rem',
            background: locationError.includes('✅') ? '#f0fdf4' : locationError.includes('⚠️') ? '#fefce8' : '#fef2f2',
            border: `1px solid ${locationError.includes('✅') ? '#bbf7d0' : locationError.includes('⚠️') ? '#fde047' : '#fecaca'}`,
            color: locationError.includes('✅') ? '#166534' : locationError.includes('⚠️') ? '#854d0e' : '#dc2626',
          }}>
            {locationError}
          </div>
        )}

     
      </div>

      {/* Divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem' }}>
        <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
        <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 600, letterSpacing: '0.05em' }}>DELIVERY DETAILS</span>
        <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
      </div>

      {/* Form fields */}
      <div className="checkout-form-fields" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
        {fields.map((row, ri) => (
          <div key={ri} className="checkout-form-row" style={{ display: 'grid', gridTemplateColumns: row.length === 1 ? '1fr' : '1fr 1fr', gap: '1rem' }}>
            {row.map(field => (
              <div key={field.name}>
                <label style={{ display: 'block', fontWeight: 500, fontSize: '0.8125rem', color: '#374151', marginBottom: '5px' }}>
                  {field.label} {field.required && <span style={{ color: '#ef4444' }}>*</span>}
                </label>
                <input
                  type={field.type}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={form[field.name] || ''}
                  onChange={handleChange}
                  required={field.required}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#16a34a'}
                  onBlur={e => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
            ))}
          </div>
        ))}

        {/* PIN + Order Notes row */}
        <div className="checkout-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, fontSize: '0.8125rem', color: '#374151', marginBottom: '5px' }}>
              PIN Code <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text" name="pin" placeholder="500001"
              value={form.pin || ''} onChange={handlePinChange}
              required maxLength={6} pattern="[0-9]{6}"
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#16a34a'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, fontSize: '0.8125rem', color: '#374151', marginBottom: '5px' }}>
              Order Notes <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              type="text" name="notes" placeholder="Special delivery instructions..."
              value={form.notes || ''} onChange={handleChange}
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#16a34a'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
        <button type="button" onClick={onBack} style={{ padding: '10px 22px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}>
          ← Back
        </button>
        <button type="submit" style={{ padding: '10px 26px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '0.875rem' }}>
          Continue to Payment →
        </button>
      </div>

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </form>
  );
}

// ── Step 2: Payment ───────────────────────────────────────────────────────────
function PaymentStep({ form, onNext, onBack, cartTotal, deliveryCharge, finalTotal, cartItems }) {
  const [method, setMethod]       = useState('online');

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext(method);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 style={{ fontWeight: 700, fontSize: '1.125rem', color: '#111827', marginBottom: '1.25rem' }}>Payment Method</h2>

      {/* ── 1 payment card (COD temporarily disabled) ── */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>

        {/* Online */}
        <button type="button" onClick={() => setMethod('online')} style={{
          padding: '1.1rem 0.875rem', border: `2px solid ${method === 'online' ? '#16a34a' : '#e5e7eb'}`,
          borderRadius: '10px', background: method === 'online' ? '#f0fdf4' : '#fff',
          cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
          minWidth: '200px'
        }}>
          <div style={{ fontSize: '1.6rem', marginBottom: '6px' }}>💳</div>
          <div style={{ fontWeight: 700, fontSize: '0.875rem', color: method === 'online' ? '#15803d' : '#111827' }}>
            Cards / UPI / Wallet
          </div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '3px' }}>
            Net Banking included
          </div>
          {method === 'online' && (
            <div style={{ marginTop: '6px', fontSize: '0.68rem', color: '#16a34a', fontWeight: 700 }}>✓ Selected</div>
          )}
        </button>

        {/* COD - Temporarily disabled */}
        {/* <button type="button" onClick={() => setMethod('cod')} style={{
          padding: '1.1rem 0.875rem', border: `2px solid ${method === 'cod' ? '#16a34a' : '#e5e7eb'}`,
          borderRadius: '10px', background: method === 'cod' ? '#f0fdf4' : '#fff',
          cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
        }}>
          <div style={{ fontSize: '1.6rem', marginBottom: '6px' }}>💵</div>
          <div style={{ fontWeight: 700, fontSize: '0.875rem', color: method === 'cod' ? '#15803d' : '#111827' }}>
            Cash on Delivery
          </div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '3px' }}>
            Pay when order arrives
          </div>
          {method === 'cod' && (
            <div style={{ marginTop: '6px', fontSize: '0.68rem', color: '#16a34a', fontWeight: 700 }}>✓ Selected</div>
          )}
        </button> */}
      </div>

    
      {/* ── Order summary ── */}
      <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', marginBottom: '1.25rem' }}>
        <div style={{ background: '#f9fafb', padding: '10px 14px', borderBottom: '1px solid #e5e7eb' }}>
          <span style={{ fontWeight: 600, fontSize: '0.8125rem', color: '#374151' }}>Order Summary</span>
        </div>
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
            <span style={{ color: '#6b7280' }}>Item Total ({cartItems.reduce((s, i) => s + i.qty, 0)} item{cartItems.reduce((s, i) => s + i.qty, 0) !== 1 ? 's' : ''})</span>
            <span style={{ color: '#374151', fontWeight: 500 }}>₹{cartTotal.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
            <span style={{ color: '#6b7280' }}>Delivery Charges</span>
            <span style={{ color: deliveryCharge > 0 ? '#374151' : '#16a34a', fontWeight: 600 }}>
              {deliveryCharge > 0 ? `₹${deliveryCharge.toFixed(2)}` : 'FREE'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
            <span style={{ color: '#6b7280' }}>GST</span>
            <span style={{ color: '#374151', fontWeight: 500 }}>₹0.00</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 800, borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '2px' }}>
            <span style={{ color: '#111827' }}>Amount to Pay</span>
            <span style={{ color: '#16a34a' }}>₹{finalTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>



      {/* ── Buttons ── */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
        <button type="button" onClick={onBack}
          style={{ padding: '11px 22px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem' }}>
          ← Back
        </button>
        <button type="submit"
          style={{ padding: '11px 28px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem', transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
          onMouseLeave={e => e.currentTarget.style.background = '#16a34a'}
        >
          {method === 'cod' ? `Place Order ₹${finalTotal.toFixed(2)}` : `Pay ₹${finalTotal.toFixed(2)} →`}
        </button>
      </div>
    </form>
  );
}


// ── Step 3: Confirmation ──────────────────────────────────────────────────────
function ConfirmationStep({ form, orderNumber, onGoHome, deliveryCharge, finalTotal, cartItems, cartTotal }) {
  const orderId = orderNumber || ('#FC' + Math.floor(100000 + Math.random() * 900000));

  const handlePrintReceipt = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      showWarning('Please allow pop-ups to print the receipt.');
      return;
    }

    const receiptHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order Receipt - ePayhub Store</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background: white; padding: 40px 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
            .receipt { max-width: 550px; width: 100%; margin: 0 auto; background: white; border-radius: 16px; padding: 30px; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1); }
            .success-icon { width: 70px; height: 70px; border-radius: 50%; background: #f0fdf4; border: 3px solid #16a34a; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; }
            .success-icon svg { width: 34px; height: 34px; }
            h1 { font-size: 1.75rem; font-weight: 800; color: #111827; text-align: center; margin-bottom: 8px; }
            .subtitle { text-align: center; color: #6b7280; margin-bottom: 24px; font-size: 0.9rem; }
            .order-id { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 10px 20px; text-align: center; margin-bottom: 28px; }
            .order-id span { font-weight: 700; color: #15803d; font-size: 1rem; }
            .section { border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; margin-bottom: 20px; }
            .section-title { font-weight: 700; font-size: 1rem; color: #111827; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid #f3f4f6; }
            .info-row { display: flex; margin-bottom: 10px; font-size: 0.875rem; }
            .info-label { color: #6b7280; width: 100px; flex-shrink: 0; }
            .info-value { font-weight: 500; color: #111827; }
            .items-list { display: flex; flex-direction: column; gap: 12px; }
            .item { display: flex; align-items: center; gap: 12px; }
            .item-img { width: 48px; height: 48px; object-fit: cover; border-radius: 8px; border: 1px solid #e5e7eb; flex-shrink: 0; }
            .item-details { flex: 1; min-width: 0; }
            .item-name { font-size: 0.8125rem; font-weight: 600; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .item-qty { font-size: 0.75rem; color: #9ca3af; }
            .item-price { font-weight: 700; font-size: 0.875rem; color: #111827; flex-shrink: 0; }
            .summary-row { display: flex; justify-content: space-between; font-size: 0.875rem; color: #6b7280; padding: 4px 0; }
            .summary-row span:last-child { color: #374151; font-weight: 500; }
            .summary-row.free span:last-child { color: #16a34a; font-weight: 600; }
            .total-row { display: flex; justify-content: space-between; padding-top: 10px; margin-top: 8px; border-top: 2px solid #e5e7eb; font-weight: 800; font-size: 1rem; color: #111827; }
            .footer { text-align: center; margin-top: 20px; padding-top: 20px; border-top: 1px dashed #e5e7eb; font-size: 0.75rem; color: #9ca3af; }
            .thank-you { text-align: center; margin-top: 16px; font-weight: 600; color: #16a34a; }
            @media print { body { padding: 0; background: white; } .receipt { box-shadow: none; padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="success-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" strokeWidth="3" stroke="#16a34a" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M5 12l5 5l10 -10"/>
              </svg>
            </div>
            <h1>Order Placed Successfully! 🎉</h1>
            <div class="subtitle">Thank you, <strong>${form.firstName || ''} ${form.lastName || ''}</strong>!</div>
            <div class="order-id"><span>Order ID: ${orderId}</span></div>
            <div class="section">
              <div class="section-title">📦 Delivery Address</div>
              <div class="info-row"><div class="info-label">Address:</div><div class="info-value">${form.address1 || ''}${form.address2 ? ', ' + form.address2 : ''}</div></div>
              <div class="info-row"><div class="info-label">City:</div><div class="info-value">${form.city || ''}</div></div>
              <div class="info-row"><div class="info-label">State:</div><div class="info-value">${form.state || ''}</div></div>
              <div class="info-row"><div class="info-label">PIN Code:</div><div class="info-value">${form.pin || ''}</div></div>
              ${form.landmark ? `<div class="info-row"><div class="info-label">Landmark:</div><div class="info-value">${form.landmark}</div></div>` : ''}
              <div class="info-row"><div class="info-label">Phone:</div><div class="info-value">${form.phone || 'N/A'}</div></div>
            </div>
            <div class="section">
              <div class="section-title">🛍️ Items Purchased</div>
              <div class="items-list">
                ${cartItems.map(item => `
                  <div class="item">
                    <img src="${item.image}" alt="${item.name}" class="item-img" />
                    <div class="item-details">
                      <div class="item-name">${item.name}${item.unit ? ` (${item.unit})` : ''}</div>
                      <div class="item-qty">Qty: ${item.qty} × ₹${item.price}</div>
                    </div>
                    <div class="item-price">₹${(item.price * item.qty).toFixed(2)}</div>
                  </div>
                `).join('')}
              </div>
              <div style="margin-top:10px; border-top: 1px solid #e5e7eb; padding-top:10px;">
                <div class="summary-row"><span>Item Total (${cartItems.length} item${cartItems.length !== 1 ? 's' : ''})</span><span>₹${cartTotal.toFixed(2)}</span></div>
                <div class="summary-row ${deliveryCharge > 0 ? '' : 'free'}"><span>Delivery Charges</span><span>${deliveryCharge > 0 ? `₹${deliveryCharge.toFixed(2)}` : 'FREE'}</span></div>
                <div class="summary-row"><span>GST</span><span>₹0.00</span></div>
                <div class="total-row"><span>Grand Total</span><span>₹${finalTotal.toFixed(2)}</span></div>
              </div>
            </div>
            <div class="thank-you">🌟 Thank you for shopping with ePayhub Store!</div>
            <div class="footer">This is a system generated receipt. No signature required.<br/>For support, contact kilomart685@gmail.com</div>
          </div>
          <script>
            window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 500); };
          <\/script>
        </body>
      </html>
    `;

    printWindow.document.write(receiptHTML);
    printWindow.document.close();
  };

  return (
    <div style={{ textAlign: 'center' }}>
      {/* Success Animation */}
      <div style={{ 
        width: 80, 
        height: 80, 
        borderRadius: '50%', 
        background: '#f0fdf4', 
        border: '3px solid #16a34a', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        margin: '0 auto 1.5rem',
        animation: 'scaleUp 0.5s ease-out'
      }}>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="36" 
          height="36" 
          viewBox="0 0 24 24" 
          strokeWidth="3" 
          stroke="#16a34a" 
          fill="none" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          style={{ animation: 'checkmark 0.5s ease-out 0.2s both' }}
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M5 12l5 5l10 -10"/>
        </svg>
      </div>

      {/* Success Message */}
      <div style={{ animation: 'fadeInUp 0.5s ease-out' }}>
        <h2 style={{ fontWeight: 800, fontSize: '1.5rem', color: '#111827', marginBottom: '8px' }}>
          Order Placed Successfully! 🎉
        </h2>
        <p style={{ color: '#6b7280', marginBottom: '0.5rem' }}>
          Thank you, <strong>{form.firstName} {form.lastName}</strong>!
        </p>
        {/* <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
          A confirmation has been sent to <strong>{form.email}</strong>
        </p> */}
      </div>

      {/* Order ID */}
      <div style={{ 
        display: 'inline-block', 
        background: '#f0fdf4', 
        border: '1px solid #bbf7d0', 
        borderRadius: '10px', 
        padding: '10px 24px', 
        marginBottom: '2rem',
        animation: 'fadeInUp 0.5s ease-out 0.1s both'
      }}>
        <span style={{ fontWeight: 700, color: '#15803d', fontSize: '1rem' }}>Order ID: {orderId}</span>
      </div>

      {/* Order Summary with Product Images */}
      <div style={{ 
        border: '1px solid #e5e7eb', 
        borderRadius: '12px', 
        padding: '1.5rem', 
        maxWidth: 600, 
        margin: '0 auto 1.5rem', 
        textAlign: 'left',
        background: '#fff',
        animation: 'fadeInUp 0.5s ease-out 0.2s both'
      }}>
        <h3 style={{ fontWeight: 700, fontSize: '1rem', color: '#111827', marginBottom: '1rem', paddingBottom: '8px', borderBottom: '2px solid #16a34a', display: 'inline-block' }}>
          Order Summary
        </h3>

        {/* Products Grid with Images */}
        <div style={{ marginBottom: '1.5rem', marginTop: '1rem' }}>
          <h4 style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', marginBottom: '12px' }}>Items Purchased:</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {cartItems.map((item, idx) => (
              <div 
                key={item.id} 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  padding: '12px',
                  background: '#f9fafb',
                  borderRadius: '10px',
                  transition: 'transform 0.2s',
                  animation: `slideInRight 0.4s ease-out ${0.3 + idx * 0.1}s both`
                }}
              >
                {/* Product Image */}
                <img 
                  src={item.image} 
                  alt={item.name} 
                  style={{ 
                    width: 60, 
                    height: 60, 
                    objectFit: 'cover', 
                    borderRadius: '10px', 
                    border: '1px solid #e5e7eb',
                    flexShrink: 0,
                    background: '#fff'
                  }} 
                />
                
                {/* Product Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: 600, 
                    color: '#111827', 
                    marginBottom: '4px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.name}{item.unit ? ` (${item.unit})` : ''}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '4px' }}>
                    {item.category}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    Quantity: {item.qty} × ₹{item.price}
                  </div>
                </div>
                
                {/* Item Total */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#111827' }}>
                    ₹{(item.price * item.qty).toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Address */}
        <div style={{ marginBottom: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
          <h4 style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', marginBottom: '12px' }}>Delivery Address:</h4>
          <div style={{ 
            background: '#f9fafb', 
            padding: '12px', 
            borderRadius: '8px',
            fontSize: '0.875rem',
            color: '#4b5563',
            lineHeight: '1.5'
          }}>
            <div><strong>{form.firstName} {form.lastName}</strong></div>
            <div>{form.address1 || ''}</div>
            {form.address2 && <div>{form.address2}</div>}
            <div>{form.city || 'Hyderabad'}, {form.state || 'Telangana'} - {form.pin || ''}</div>
            {form.landmark && <div>📍 Landmark: {form.landmark}</div>}
            <div>📞 {form.phone || 'N/A'}</div>
          </div>
        </div>

        {/* Price Summary */}
        <div style={{ 
          background: '#f0fdf4', 
          borderRadius: '10px', 
          padding: '1rem',
          marginTop: '1rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Item Total ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''}):</span>
            <span style={{ fontWeight: 500, color: '#111827' }}>₹{cartTotal.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Delivery Charges:</span>
            <span style={{ color: '#16a34a', fontWeight: 600 }}>{deliveryCharge > 0 ? `₹${deliveryCharge.toFixed(2)}` : 'FREE'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>GST:</span>
            <span style={{ color: '#111827' }}>₹0.00</span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            paddingTop: '8px', 
            marginTop: '8px', 
            borderTop: '2px solid #bbf7d0',
            fontWeight: 700,
            fontSize: '1rem',
            color: '#15803d'
          }}>
            <span>Grand Total:</span>
            <span>₹{finalTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        animation: 'fadeInUp 0.5s ease-out 0.4s both'
      }}>
        {/* <button 
          onClick={onGoHome} 
          style={{ 
            padding: '12px 28px', 
            background: '#16a34a', 
            color: 'white', 
            border: 'none', 
            borderRadius: '8px', 
            fontWeight: 600, 
            cursor: 'pointer', 
            fontSize: '0.875rem',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#15803d'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#16a34a'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-5v-7H7v7H2a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          Continue Shopping
        </button> */}
        <button 
          onClick={handlePrintReceipt} 
          style={{ 
            padding: '12px 24px', 
            background: '#fff', 
            color: '#374151', 
            border: '1px solid #d1d5db', 
            borderRadius: '8px', 
            fontWeight: 600, 
            cursor: 'pointer', 
            fontSize: '0.875rem',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9V3h12v6"/>
            <path d="M6 21H4a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2"/>
            <path d="M6 15h12"/>
            <path d="M6 18h12"/>
          </svg>
          🖨 Print Receipt
        </button>
      </div>

      {/* Animation Styles */}
      <style>
        {`
          @keyframes scaleUp {
            from {
              transform: scale(0);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          @keyframes checkmark {
            from {
              stroke-dashoffset: 100;
              opacity: 0;
            }
            to {
              stroke-dashoffset: 0;
              opacity: 1;
            }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
        `}
      </style>
    </div>
  );
}

// ── Main CheckoutPage ─────────────────────────────────────────────────────────
export default function CheckoutPage() {
  const [step, setStep] = useState(0);
  const [deliveryForm, setDeliveryForm] = useState({ deliveryType: 'standard' });
  const [orderNumber, setOrderNumber] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  // Snapshot cart data before clearing so ConfirmationStep has correct values
  const [orderSnapshot, setOrderSnapshot] = useState(null);
  const { cartTotal, cartItems, clearCart, getDeliveryCharge, getFinalTotal, userType } = useCart();
  const deliveryCharge = getDeliveryCharge();
  const finalTotal = getFinalTotal();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const currentUser = localStorage.getItem('currentUser');
    if (!token || !currentUser) {
      showError('Please sign in to continue with checkout.');
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const handleGoHome = () => {
    navigate('/');
    window.scrollTo(0, 0);
  };

  // ── Called when user clicks "Place Order" in PaymentStep ───
  const handlePlaceOrder = async (paymentMethod) => {
    setOrderLoading(true);
    try {
      // Get email from logged-in user since we removed email field from form
      let userEmail = deliveryForm.email || '';
      if (!userEmail) {
        try {
          const cu = JSON.parse(localStorage.getItem('currentUser') || '{}');
          userEmail = cu.email || '';
        } catch {}
      }

      console.log('👤 Logged-in user email from localStorage:', userEmail);

      // Determine order type based on cart contents
      const hasB2BItems = cartItems.some(item => item.isB2B);
      const hasB2CItems = cartItems.some(item => !item.isB2B);
      let orderUserType = 'b2c'; // default
      
      if (hasB2BItems && !hasB2CItems) {
        orderUserType = 'b2b'; // Only B2B items
      } else if (hasB2BItems && hasB2CItems) {
        orderUserType = 'mixed'; // Mixed cart
      }

      const orderBody = {
        full_name:      `${deliveryForm.firstName || ''} ${deliveryForm.lastName || ''}`.trim(),
        email:          userEmail,
        phone:          deliveryForm.phone     || '',
        address:        `${deliveryForm.address1 || ''} ${deliveryForm.address2 || ''}`.trim(),
        city:           deliveryForm.city      || 'Hyderabad',
        pincode:        deliveryForm.pin       || '',
        delivery_area:  deliveryForm.landmark  || '',
        payment_method: paymentMethod          || 'cod',
        user_type:      orderUserType,
        notes:          deliveryForm.notes     || '',
        items: cartItems.map(item => ({
          id:       item.id,
          name:     item.name,
          category: item.category || '',
          price:    item.price,
          quantity: item.qty,
          image:    item.image || '',
          is_b2b:   item.isB2B || false, // Add B2B flag to each item
        })),
      };

      // ── COD: place order immediately ──────────────────────────
      if (paymentMethod === 'cod') {
        const data = await placeOrder(orderBody);
        if (data.success) {
          setOrderSnapshot({
            items:          [...cartItems],
            cartTotal:      cartTotal,
            deliveryCharge: deliveryCharge,
            finalTotal:     finalTotal,
          });
          setOrderNumber(data.order?.order_number || '#KM000000');
          clearCart(false); // Clear B2C cart
          clearCart(true);  // Clear B2B cart
          setStep(3);
        } else {
          showError(data.message || 'Failed to place order. Please try again.');
        }
        return;
      }

      // ── Online payment: get Omniware URL first, place order only after payment ──
      // Store delivery form in sessionStorage so callback can place the order
      sessionStorage.setItem('pendingOrder', JSON.stringify(orderBody));
      sessionStorage.setItem('pendingCartSnapshot', JSON.stringify({
        items:          [...cartItems],
        cartTotal:      cartTotal,
        deliveryCharge: deliveryCharge,
        finalTotal:     finalTotal,
      }));

      // Generate a temp order reference for Omniware (not saved to DB yet)
      const tempRef = 'KM' + Date.now().toString(36).toUpperCase();

      const payData = await createPayment({
        order_id:    tempRef,
        amount:      finalTotal.toFixed(2),
        name:        orderBody.full_name,
        email:       orderBody.email,
        phone:       orderBody.phone,
        city:        orderBody.city,
        zip_code:    orderBody.pincode,
        description: `ePayhub Store Order ${tempRef}`,
      });

      console.log('📧 Payment API payload email:', orderBody.email);

      if (payData.success && payData.payment_url) {
        // DON'T clear cart here - only clear after successful payment confirmation
        window.location.href = payData.payment_url;
      } else {
        // Clean up session if payment URL failed
        sessionStorage.removeItem('pendingOrder');
        sessionStorage.removeItem('pendingCartSnapshot');
        showError(payData.message || 'Failed to initiate payment. Please try again.');
      }

    } catch (err) {
      console.error('Order error:', err);
      showError('Unable to reach the server. Please check your connection.');
    } finally {
      setOrderLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 0' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={handleGoHome} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <img src="/favicon.svg" alt="" width={30} height={30} style={{ display: 'block', borderRadius: 7 }} />
            <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#16a34a' }}>ePayhub Store</span>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: '#6b7280' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" strokeWidth="2" stroke="#16a34a" fill="none"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-6z"/><path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0"/><path d="M8 11v-4a4 4 0 1 1 8 0v4"/></svg>
            Secure Checkout
          </div>
        </div>
      </div>

      <div className="checkout-container container" style={{ padding: '2.5rem 1rem', maxWidth: 860 }}>
        <StepIndicator current={step} />

        {/* Order loading overlay */}
        {orderLoading && (
          <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 999,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white', borderRadius: '16px',
              padding: '2rem 2.5rem', textAlign: 'center',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              maxWidth: '90vw'
            }}>
              <div style={{
                width: 48, height: 48, border: '4px solid #16a34a',
                borderTop: '4px solid transparent', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem'
              }} />
              <p style={{ fontWeight: 700, color: '#1f2937', fontSize: '1rem' }}>Placing your order...</p>
              <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '4px' }}>Please wait</p>
            </div>
          </div>
        )}

        <div className="checkout-card" style={{ background: '#fff', borderRadius: '12px', padding: '2rem', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
          {step === 0 && <CartReviewStep onNext={() => setStep(1)} onBack={handleGoHome} />}
          {step === 1 && <DeliveryStep form={deliveryForm} setForm={setDeliveryForm} onNext={() => setStep(2)} onBack={() => setStep(0)} />}
          {step === 2 && <PaymentStep form={deliveryForm} onNext={handlePlaceOrder} onBack={() => setStep(1)} cartTotal={cartTotal} deliveryCharge={deliveryCharge} finalTotal={finalTotal} cartItems={cartItems} />}
          {step === 3 && <ConfirmationStep form={deliveryForm} orderNumber={orderNumber} onGoHome={handleGoHome} deliveryCharge={orderSnapshot?.deliveryCharge ?? deliveryCharge} finalTotal={orderSnapshot?.finalTotal ?? finalTotal} cartItems={orderSnapshot?.items ?? cartItems} cartTotal={orderSnapshot?.cartTotal ?? cartTotal} />}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}